import React, { useEffect, useState } from "react";
import DateFormatter from "../../../app/formatters/DateFormatter";
import DateLogic from "../../../app/logics/DateLogic";
import * as datefns from "date-fns";
import { getShowHrefUrl, getAddHrefUrl, getCloseUrl, getReOpenUrl, getValidateUrl } from "./RpaUrlHelper";
import _ from "lodash";

/** @typedef {import('../models/RpaResourceModel.js').RpaResourceItem } RpaResourceItem */
/** @typedef {import('../services/RpaPlanningService.js').ModifiedRpaScheduleGridOfCurrentDayItem } ModifiedRpaScheduleGridOfCurrentDayItem */
/** @typedef {import('../models/RpaScheduleGridModel.js').RpaScheduleGridItem } RpaScheduleGridItem */
/** @typedef {import('../models/RpaReservationModel.js').RpaReservationItem } RpaReservationItem */
/** @typedef {import('../models/RpaInfoScheduleGridModel.js').RpaInfoScheduleGridItem } RpaInfoScheduleGridItem */
/** @typedef {import('../../../app/models/UserModel.js').UserItem } UserItem */
/** @typedef {import('../../../app/models/CloseDayModel.js').CloseDayItem } CloseDayItem */
/** @typedef { import('../../../jsdoc/bottom.jsdoc.js').ModifiedWindow } ModifiedWindow */

/**
 * @typedef {object} RpaPlanningCaseParams
 * @property {import('moment').Moment} dayDate - Date du jour
 * @property {RpaResourceItem} rpaResource - Ressource RPA
 * @property {ModifiedRpaScheduleGridOfCurrentDayItem} rpaScheduleGrid - Plage horaire de la ressource
 * @property {boolean} transportAndParkingOption - Option de transport et parking
 * @property {Array<RpaReservationItem>} rpaReservations - Réservations de la plage horaire
 * @property {boolean} showScheduleEndHourOption - Afficher l'heure de fin de la plage horaire
 * @property {boolean} showAvailableReservationsOption - Afficher le nombre de réservations disponibles
 * @property {boolean} showFirstReservationOnCaseOption - Afficher la première réservation sur la case
 * @property {boolean} showNbTotalPersonsOption - Afficher le nombre total de personnes
 * @property {'front'|'back'} view - Vue front ou back
 * @property {UserItem} connectedUser - Utilisateur connecté
 * @property {Array<CloseDayItem>} closeDays - Jours de fermetures
 * @property {string} dayStr - Jour sous forme de chaîne DD/MM/YYYY
 */

/**
 * @param {RpaPlanningCaseParams} props
 */
export default function RpaPlanningCase({ dayDate, rpaResource, rpaScheduleGrid, transportAndParkingOption, rpaReservations, showScheduleEndHourOption, showAvailableReservationsOption, showFirstReservationOnCaseOption, showNbTotalPersonsOption, view, connectedUser, closeDays, dayStr }) {
  var paragrapheLineHourClass = view === "back" ? "resaplan__case-info-paragraph resaplan__case-info-paragraph--light" : "resaplan__case-info-paragraph";
  /**
   * Retourne la classe CSS de la case de planning
   * @param {string} view - Vue back ou front
   * @param {RpaResourceItem} rpaResource - Ressource
   * @param {ModifiedRpaScheduleGridOfCurrentDayItem} rpaScheduleGrid - Date Moment de la plage horaire
   * @param {Array<RpaReservationItem>} rpaReservations - Réservations
   * @returns {string} - Classe CSS de la case de planning
   */
  const getResaPlanCaseStr = (view, rpaResource, rpaScheduleGrid, rpaReservations) => {
    var str = "resaplan__case";

    if (view === "back") {
      str += " resaplan__case--with-hover";
      if (rpaScheduleGrid.passed && rpaReservations.length && !rpaScheduleGrid.checked) {
        str += " resaplan__case--marked-danger";
      } else if (rpaScheduleGrid.passed && !rpaReservations.length && !rpaScheduleGrid.checked) {
        str += " resaplan__case--passed";
      } else if (rpaScheduleGrid.checked) {
        str += " resaplan__case--marked-success";
      }

      if (rpaScheduleGrid.date && !rpaScheduleGrid.repeat_each_week) {
        str += " resaplan__case--onetime";
      }
    } else {
      if ((rpaReservations.length && !rpaResource.multi_resas) || (rpaResource.multi_resas && rpaReservations.length >= rpaResource.max_number_with_multi_resas)) {
        str += " resaplan__case--marked-danger";
      }
      if (rpaScheduleGrid.passed) {
        str += " resaplan__case--passed";
      }
    }

    return str;
  };

  /**
   * Retourne les réservations de la plage horaire
   * @param {ModifiedRpaScheduleGridOfCurrentDayItem} rpaScheduleGrid - Plage horaire
   * @param {boolean} showFirstReservationOnCaseOption - Afficher la première réservation sur la case
   * @returns {{strName: string, totalNbPersons: number, rpaReservations: Array<RpaReservationItem>}} - Réservations de la plage horaire
   */
  const getRpaReservations = (rpaScheduleGrid, showFirstReservationOnCaseOption) => {
    return {
      strName: showFirstReservationOnCaseOption ? rpaScheduleGrid.reservationStrName : "",
      totalNbPersons: rpaScheduleGrid.totalNbPersons,
      rpaReservations
    };
  };

  /**
   * Retourne les réservations de la plage horaire pour la partie back
   * @param {ModifiedRpaScheduleGridOfCurrentDayItem} rpaScheduleGrid - Plage horaire
   * @param {boolean} showFirstReservationOnCaseOption - Option d'affichage de la première réservation sur la case
   * @param {boolean} showNbTotalPersonsOption - Option d'affichage du nombre total de personnes
   * @returns {?JSX.Element} - Réservations de la plage horaire pour la partie back
   */
  const renderBackReservations = (rpaScheduleGrid, showFirstReservationOnCaseOption, showNbTotalPersonsOption) => {
    var rpaReservationsObj = getRpaReservations(rpaScheduleGrid, showFirstReservationOnCaseOption);

    if (rpaReservationsObj.rpaReservations.length) {
      if (showFirstReservationOnCaseOption) {
        return (
          <>
            <div className="resaplan__case-info-line">
              <p className="resaplan__case-info-paragraph resaplan__case-info-paragraph--weight">{rpaReservationsObj.strName}</p>
            </div>
            {showNbTotalPersonsOption && (
              <div className="resaplan__case-info-line">
                <p className="resaplan__case-info-paragraph">{rpaReservationsObj.rpaReservations.length} réservation(s)</p>
                <p className="resaplan__case-info-paragraph">{rpaReservationsObj.totalNbPersons} personne(s)</p>
              </div>
            )}
          </>
        );
      } else if (showNbTotalPersonsOption) {
        return (
          <div className="resaplan__case-info-line">
            <p className="resaplan__case-info-paragraph">{rpaReservationsObj.rpaReservations.length} réservation(s)</p>
            <p className="resaplan__case-info-paragraph">{rpaReservationsObj.totalNbPersons} personne(s)</p>
          </div>
        );
      }
    }

    return null;
  };

  /**
   * Retourne les réservations de la plage horaire pour la partie front
   * @param {RpaResourceItem} rpaResource - Ressource
   * @param {Array<RpaReservationItem>} rpaReservations - Réservations
   * @param {UserItem} connectedUser - Utilisateur connecté
   * @returns {JSX.Element} - Réservations de la plage horaire pour la partie front
   */
  const renderFrontReservations = (rpaResource, rpaReservations, connectedUser) => {
    var reservationOfUser = rpaReservations?.length && connectedUser && rpaReservations.find(reservation => reservation.userId === connectedUser.id);
    return (
      <div>
        {rpaResource.multi_resas && rpaReservations.length < rpaResource.max_number_with_multi_resas ? (
          reservationOfUser ? (
            <div className="resaplan__case__info">
              <div className="resaplan__case-info-line">
                <div className="resaplan__case-info-paragraph">
                  <div className="badge badge--small badge--icon badge--success">Inscrit</div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="space--8"></div>
              <a data-loader="true" className="resaplan__case-btn" href={getAddHrefUrl(view, dayStr, rpaResource, rpaScheduleGrid, transportAndParkingOption)}>
                <span className="material-icons">how_to_reg</span>
              </a>
            </div>
          )
        ) : rpaReservations.length ? (
          <div>
            <div className="resaplan__case__info">
              <div className="resaplan__case-info-line">
                <p className="resaplan__case-info-paragraph">Complet</p>
              </div>
            </div>
            {rpaReservations[0].userId === connectedUser?.id && (
              <div className="resaplan__case__info">
                <div className="resaplan__case-info-line">
                  <div className="resaplan__case-info-paragraph">
                    <div className="badge badge--small badge--icon badge--success">Inscrit</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="space--8"></div>
            <a data-loader="true" className="resaplan__case-btn" href={getAddHrefUrl(view, dayStr, rpaResource, rpaScheduleGrid, transportAndParkingOption)}>
              <span className="material-icons">how_to_reg</span>
            </a>
          </div>
        )}
      </div>
    );
  };

  const renderBtns = () => {
    return (
      <>
        {scheduleGridIsAvailable(rpaScheduleGrid, closeDays) ? (
          <>
            {view === "back" ? (
              <div className="resaplan__case__btns">
                <a data-loader="true" data-tooltip="Ajouter une réservation" className="resaplan__case-btn" href={getAddHrefUrl(view, dayStr, rpaResource, rpaScheduleGrid, transportAndParkingOption)}>
                  <span className="material-icons">add</span>
                </a>
                {connectedUser.right <= 1 ? (
                  <>
                    <a href={`/_delete/rpa-schedule-grid/${rpaScheduleGrid.id}`} data-tooltip="Supprimer cette plage horaire" className="resaplan__case-btn resaplan__case-btn--danger resaplan__case-btn--visible-on-hover" data-dialog-action-title="Oui, Supprimer" data-dialog="true" data-dialog-message="Êtes-vous sûr ? Si vous supprimez cet horaire, les réservations sur cet horaire seront supprimées. Les resources/activités utilisant cet horaire seront également impactées." onClick={e => onClickOnDeleteBtn(e)}>
                      <span className="material-icons">delete</span>
                    </a>
                    <a href={`/_edit/rpa-schedule-grid/${rpaScheduleGrid.id}?onlyfields=start_hour,end_hour,title,price_type,reservation_type`} data-tooltip="Modifier cette plage horaire" className="resaplan__case-btn resaplan__case-btn--visible-on-hover">
                      <span className="material-icons">edit</span>
                    </a>
                  </>
                ) : (
                  ""
                )}
                {rpaScheduleGrid.couldBeReopenOrClose ? (
                  <>
                    {rpaScheduleGrid.closed ? (
                      <a data-loader="true" data-tooltip="Ouvrir cette plage horaire" className="resaplan__case-btn resaplan__case-btn--visible-on-hover" href={getReOpenUrl(dayStr, rpaResource, rpaScheduleGrid)}>
                        <span className="material-icons">lock_open</span>
                      </a>
                    ) : (
                      <a data-loader="true" data-tooltip="Fermer cette plage horaire" className="resaplan__case-btn resaplan__case-btn--visible-on-hover" href={getCloseUrl(dayStr, rpaResource, rpaScheduleGrid)}>
                        <span className="material-icons">lock</span>
                      </a>
                    )}
                  </>
                ) : (
                  ""
                )}
                <a data-loader="true" data-tooltip="Valider cette plage horaire" className="resaplan__case-btn resaplan__case-btn--visible-on-hover" href={getValidateUrl(dayStr, rpaResource, rpaScheduleGrid)}>
                  <span className="material-icons">done</span>
                </a>
              </div>
            ) : (
              ""
            )}
          </>
        ) : (
          <>
            {view === "back" && connectedUser.right <= 1 ? (
              <div className="resaplan__case__btns">
                <a href={`/_delete/rpa-schedule-grid/${rpaScheduleGrid.id}`} data-tooltip="Supprimer cette plage horaire" className="resaplan__case-btn resaplan__case-btn--danger resaplan__case-btn--visible-on-hover" data-dialog-action-title="Oui, Supprimer" data-dialog="true" data-dialog-message="Êtes-vous sûr ? Si vous supprimez cet horaire, les réservations sur cet horaire seront supprimées. Les resources/activités utilisant cet horaire seront également impactées." onClick={e => onClickOnDeleteBtn(e)}>
                  <span className="material-icons">delete</span>
                </a>
                <a href={`/_edit/rpa-schedule-grid/${rpaScheduleGrid.id}?onlyfields=start_hour,end_hour,title,price_type,reservation_type`} data-tooltip="Modifier cette plage horaire" className="resaplan__case-btn resaplan__case-btn--visible-on-hover">
                  <span className="material-icons">edit</span>
                </a>
              </div>
            ) : (
              ""
            )}
          </>
        )}
      </>
    );
  };

  /**
   * Retourne si la plage horaire est disponible
   * @param {ModifiedRpaScheduleGridOfCurrentDayItem} rpaScheduleGrid - Plage horaire
   * @param {Array<CloseDayItem>} closeDays - Jours de fermetures
   * @returns {boolean} True si la plage horaire est disponible sinon false
   */
  const scheduleGridIsAvailable = (rpaScheduleGrid, closeDays) => {
    const startDate = datefns.set(dayDate.toDate(), {
      hours: parseInt(rpaScheduleGrid.start_hour.split(":")[0], 10),
      minutes: parseInt(rpaScheduleGrid.start_hour.split(":")[1], 10),
      seconds: 0,
      milliseconds: 0
    });
    const endDate = datefns.set(dayDate.toDate(), {
      hours: parseInt(rpaScheduleGrid.end_hour.split(":")[0], 10),
      minutes: parseInt(rpaScheduleGrid.end_hour.split(":")[1], 10),
      seconds: 0,
      milliseconds: 0
    });

    if (view === "back") {
      return !rpaScheduleGrid.passed;
    } else {
      if (!rpaScheduleGrid.passed) {
        if (rpaScheduleGrid.reservation_type === 0) {
          return true;
        } else if (rpaScheduleGrid.reservation_type === 3 && DateLogic.isClosedDateRange(closeDays, startDate, endDate)) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
  };

  /**
   * Gère le clic sur le bouton de suppression
   * @param {React.MouseEvent<HTMLAnchorElement>} e - L'événement du clic
   */
  const onClickOnDeleteBtn = e => {
    var buttonNode = /** @type {any} */ (e.currentTarget);

    e.preventDefault();
    e.stopPropagation();

    buttonNode.gwView = new Dialog(buttonNode);
  };

  return (
    <div
      className={getResaPlanCaseStr(view, rpaResource, rpaScheduleGrid, rpaReservations)}
      onClick={() => {
        if (view === "back") {
          window.location.href = getShowHrefUrl(dayStr, rpaResource, rpaScheduleGrid);
        }
      }}>
      {rpaReservations.length && view === "back" ? <div className="resaplan__case-reservations-pointer"></div> : ""}

      {scheduleGridIsAvailable(rpaScheduleGrid, closeDays) ? (
        <div className="resaplan__case__info">
          {rpaScheduleGrid.title ? (
            <div className="resaplan__case-info-line">
              <p className="resaplan__case-info-paragraph resaplan__case-info-paragraph--weight resaplan__case-info-paragraph--main">{rpaScheduleGrid.title}</p>
            </div>
          ) : (
            ""
          )}
          <div className="resaplan__case-info-line">
            {showScheduleEndHourOption ? (
              <p className={paragrapheLineHourClass}>
                {DateFormatter.formatHour(rpaScheduleGrid.start_hour, true, true)} à {DateFormatter.formatHour(rpaScheduleGrid.end_hour, true, true)}
              </p>
            ) : (
              <p className={paragrapheLineHourClass}>{DateFormatter.formatHour(rpaScheduleGrid.start_hour, true, true)}</p>
            )}
            {showAvailableReservationsOption && view === "back" ? (
              rpaReservations.length >= rpaResource.max_number_with_multi_resas ? (
                <p className="resaplan__case-info-paragraph resaplan__case-info-paragraph--weight resaplan__case-info-paragraph--danger">
                  {rpaReservations.length}/{rpaResource.max_number_with_multi_resas}
                </p>
              ) : (
                <p className="resaplan__case-info-paragraph resaplan__case-info-paragraph--weight resaplan__case-info-paragraph--main">
                  {rpaReservations.length}/{rpaResource.max_number_with_multi_resas}
                </p>
              )
            ) : (
              ""
            )}
          </div>
          {rpaScheduleGrid.closed ? (
            <div className="resaplan__case-info-line">
              <p className="resaplan__case-info-paragraph" style={{ color: "red" }}>
                Fermé
              </p>
            </div>
          ) : (
            ""
          )}
          {view === "back" ? renderBackReservations(rpaScheduleGrid, showFirstReservationOnCaseOption, showNbTotalPersonsOption) : renderFrontReservations(rpaResource, rpaReservations, connectedUser)}
          {renderBtns()}
        </div>
      ) : (
        <div className="resaplan__case-info">
          {rpaScheduleGrid.title ? (
            <div className="resaplan__case-info-line">
              <p className="resaplan__case-info-paragraph resaplan__case-info-paragraph--weight resaplan__case-info-paragraph--main">{rpaScheduleGrid.title}</p>
            </div>
          ) : (
            ""
          )}
          <div className="resaplan__case-info-line">
            {showScheduleEndHourOption ? (
              <p className={paragrapheLineHourClass}>
                {DateFormatter.formatHour(rpaScheduleGrid.start_hour, true, true)} à {DateFormatter.formatHour(rpaScheduleGrid.end_hour, true, true)}
              </p>
            ) : (
              <p className={paragrapheLineHourClass}>{DateFormatter.formatHour(rpaScheduleGrid.start_hour, true, true)}</p>
            )}
          </div>
          {rpaScheduleGrid.closed ? (
            <div className="resaplan__case-info-line">
              <p className="resaplan__case-info-paragraph" style={{ color: "red" }}>
                Fermé
              </p>
            </div>
          ) : (
            ""
          )}
          {view === "back" ? renderBackReservations(rpaScheduleGrid, showFirstReservationOnCaseOption, showNbTotalPersonsOption) : ""}
          {renderBtns()}
        </div>
      )}
    </div>
  );
}
